import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { PlusCircleIcon, DocumentDownloadIcon } from "@heroicons/react/outline";
import PaymentCard from "../../../../components/JewelleryPlans/common/PaymentCard";

const PaymentHistory = ({ plan, ledger = [] }) => {
  const navigate = useNavigate();
  const [filter, setFilter] = useState("all");

  // Filter payments based on status
  const filteredPayments = ledger.filter((payment) => {
    if (filter === "all") return true;
    return payment.status === filter;
  });

  const handleRecordPayment = () => {
    navigate(`/jewellery/payments/record/${plan._id}`);
  };

  // Calculate total amounts by status
  const getTotalByStatus = (status) => {
    return ledger
      .filter((p) => p.status === status)
      .reduce(
        (sum, p) => sum + (p.verifiedAmount || p.amountCollected || 0),
        0
      );
  };

  if (ledger.length === 0) {
    return (
      <div className="text-center py-12">
        <p className="text-neutral-dark mb-4">
          No payment records found for this plan
        </p>
        <button
          onClick={handleRecordPayment}
          className="inline-flex items-center px-4 py-2 bg-primary text-text-on-primary rounded-md hover:bg-primary-dark"
        >
          <PlusCircleIcon className="h-5 w-5 mr-2" />
          Record First Payment
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Actions Bar */}
      <div className="flex justify-between items-center">
        <div>
          <h3 className="text-lg font-medium text-text">Payment History</h3>
          <p className="text-sm text-neutral-dark mt-1">
            Total {ledger.length} payments recorded
          </p>
        </div>
        <div className="flex space-x-2">
          <button
            onClick={handleRecordPayment}
            className="inline-flex items-center px-4 py-2 bg-primary text-text-on-primary rounded-md hover:bg-primary-dark"
          >
            <PlusCircleIcon className="h-5 w-5 mr-2" />
            Record Payment
          </button>
          <button className="inline-flex items-center px-4 py-2 border border-neutral-light bg-background text-text rounded-md hover:bg-neutral-light">
            <DocumentDownloadIcon className="h-5 w-5 mr-2" />
            Export
          </button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-green-50 rounded-lg p-4">
          <p className="text-sm text-green-600 mb-1">Verified</p>
          <p className="text-2xl font-bold text-green-900">
            ₹{getTotalByStatus("VERIFIED").toLocaleString()}
          </p>
          <p className="text-xs text-green-600 mt-1">
            {ledger.filter((p) => p.status === "VERIFIED").length} payments
          </p>
        </div>
        <div className="bg-yellow-50 rounded-lg p-4">
          <p className="text-sm text-yellow-600 mb-1">Pending Review</p>
          <p className="text-2xl font-bold text-yellow-900">
            ₹{getTotalByStatus("PENDING_REVIEW").toLocaleString()}
          </p>
          <p className="text-xs text-yellow-600 mt-1">
            {ledger.filter((p) => p.status === "PENDING_REVIEW").length}{" "}
            payments
          </p>
        </div>
        <div className="bg-red-50 rounded-lg p-4">
          <p className="text-sm text-red-600 mb-1">Rejected</p>
          <p className="text-2xl font-bold text-red-900">
            ₹{getTotalByStatus("REJECTED").toLocaleString()}
          </p>
          <p className="text-xs text-red-600 mt-1">
            {ledger.filter((p) => p.status === "REJECTED").length} payments
          </p>
        </div>
      </div>

      {/* Filter Tabs */}
      <div className="border-b border-neutral-light">
        <nav className="-mb-px flex space-x-8">
          {["all", "VERIFIED", "PENDING_REVIEW", "REJECTED"].map((status) => (
            <button
              key={status}
              onClick={() => setFilter(status)}
              className={`py-2 px-1 border-b-2 font-medium text-sm
                ${
                  filter === status
                    ? "border-primary text-primary"
                    : "border-transparent text-neutral-dark hover:text-text hover:border-neutral-light"
                }`}
            >
              {status === "all" ? "All Payments" : status.replace("_", " ")}
              <span className="ml-2 text-xs">
                (
                {status === "all"
                  ? ledger.length
                  : ledger.filter((p) => p.status === status).length}
                )
              </span>
            </button>
          ))}
        </nav>
      </div>

      {/* Payments List */}
      {filteredPayments.length === 0 ? (
        <div className="text-center py-8">
          <p className="text-neutral-dark">
            No payments match the selected filter
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {filteredPayments.map((payment) => (
            <PaymentCard
              key={payment._id}
              payment={{
                ...payment,
                // Add any missing fields expected by PaymentCard
                amount: payment.verifiedAmount || payment.amountCollected,
                paymentDate: payment.dateCollected || payment.createdAt,
                paymentId: payment.paymentNumber,
                installment: payment.allocations?.[0]?.sequenceNumber,
              }}
              showPlan={false}
              onClick={() => navigate(`/jewellery/payments/${payment._id}`)}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default PaymentHistory;
